
import React, { Component } from 'react';
import { observable, action, computed } from 'mobx';
import { observer } from 'mobx-react';

import { Modal, ModalProps } from '@instructure/ui-modal';
import { CloseButton, Button, ButtonProps } from '@instructure/ui-buttons';
import { Heading } from '@instructure/ui-heading';

import { ControlledPromise } from '@matchlighter/common_library/lib/promises'

const ModalHeader = (Modal as any).Header;
const ModalBody = (Modal as any).Body;
const ModalFooter = (Modal as any).Footer;

interface PromptOptions {
    title: React.ReactNode,
    label?: string,
    body: React.ReactNode,
    size?: ModalProps['size'],
    actions: {
        [key: string]: {
            element: React.ReactElement;
            callback?: (close: () => void) => void | Promise<void>;
        }
    };
    callback?: (action: string, close: () => void) => void | Promise<void>;
}

class Prompt {
    constructor(readonly manager: PromptManager, readonly options: PromptOptions) {

    }

    readonly promise = new ControlledPromise<string>();

    get actions() { return this.options['actions'] }
    get callback() { return this.options['callback'] }
    get title() { return this.options['title'] }
    get size() { return this.options['size'] }
    get label() { return this.options['label'] }
    get body() { return this.options['body'] }

    close(reason?: string) {
        this.manager.closeModal(this, reason);
    }
}

export class PromptManager {

    @observable.shallow
    private promptStack: Prompt[] = [];

    @computed
    get currentPrompt() {
        return this.promptStack[this.promptStack.length - 1];
    }

    @action
    showModal(p: PromptOptions) {
        const ip = new Prompt(this, p);
        this.promptStack.push(ip);
        return ip;
    }

    async alert(body: React.ReactNode, { ...options }: Partial<PromptOptions> & {} = {}) {
        const opts: PromptOptions = {
            title: 'Information',
            body: body,
            actions: {
                'close': {
                    element: <Button color="primary">OK</Button>
                }
            },
        }
        Object.assign(opts, options);
        const prompt = this.showModal(opts);
        await prompt.promise;
    }

    async confirm(body: React.ReactNode, { confirmStyle = 'primary', confirmName = 'OK', ...options }: Partial<PromptOptions> & {
        confirmStyle?: ButtonProps['color'],
        confirmName?: string,
    } = {}) {
        const opts: PromptOptions = {
            title: 'Confirm',
            body: body,
            actions: {
                'close': {
                    element: <Button>Cancel</Button>
                },
                'confirm': {
                    element: <Button color={confirmStyle}>{confirmName}</Button>
                },
            },
            callback: (action, close) => close(),
        }
        Object.assign(opts, options);
        const prompt = this.showModal(opts);
        const result = await prompt.promise;
        return result == 'confirm';
    }

    closeModal(p?: Prompt, reason?: string) {
        if (p) {
            this.promptStack = this.promptStack.filter(v => v != p);
        } else {
            p = this.currentPrompt;
            this.promptStack.pop();
        }
        if (p) {
            p.promise.resolve(reason);
        }
    }

    render() {
        return <PromptContainer manager={this} />
    }
}

@observer
class InnerPromptContainer extends Component<{ manager: PromptManager }> {

    @observable
    private isWorking: boolean = false;

    private get prompt() { return this.props.manager.currentPrompt }
    private lastPrompt: Prompt;

    @action.bound
    closePrompt(action?: string) { this.props.manager.closeModal(null, action) }

    @action.bound
    handleAction(actionKey: string) {
        if (this.isWorking) return;
        const p = this.prompt;
        const actSpec = p.actions && p.actions[actionKey];
        const closeFunc = () => this.closePrompt(actionKey);

        const invokeCallback = (cb, ...args) => {
            const prom: Promise<any> = cb(...args, closeFunc);
            if (prom) {
                this.isWorking = true;
                prom.finally(action(() => {
                    this.isWorking = false;
                }));
            }
        }

        let resolved = false;

        if (actSpec && actSpec.callback) {
            resolved = true;
            invokeCallback(actSpec.callback);
        }

        if (p.callback) {
            resolved = true;
            invokeCallback(p.callback, actionKey);
        }

        if (!resolved) {
            if (actionKey == 'close') {
                this.closePrompt(actionKey);
            } else {
                console.warn(`Unregistered action "${actionKey}" called on prompt`);
            }
        }
    }

    @action.bound
    handleCloseClick() {
        this.handleAction('close');
    }

    renderCloseButton() {
        return (
            <CloseButton
                placement="end"
                offset="medium"
                screenReaderLabel="Close"
                onClick={this.handleCloseClick}
            />
        )
    }

    render() {
        const manager = this.props.manager;
        const currentPrompt = manager.currentPrompt;
        if (currentPrompt != null) {
            this.lastPrompt = currentPrompt;
        }

        const p = this.lastPrompt;
        if (!p) return null;
        const title = typeof p.title == 'string' ? <Heading>{p.title}</Heading> : p.title;

        const buttons = Object.entries(p.actions).map(([key, action]) => {
            return <React.Fragment>
                &nbsp;
                {React.cloneElement(action.element, {
                    key,
                    disabled: this.isWorking,
                    onClick: (event) => this.handleAction(key),
                })}
            </React.Fragment>
        })

        return (
            <Modal
                open={!!currentPrompt}
                size={p.size}
                label={`Modal Dialog: ${p.label || p.title}`}
                shouldCloseOnDocumentClick
            >
                <ModalHeader>
                    {this.renderCloseButton()}
                    {title}
                </ModalHeader>
                <ModalBody>
                    {p.body}
                </ModalBody>
                <ModalFooter>
                    {buttons}
                </ModalFooter>
            </Modal>
        )
    }
}

export const PromptContext = React.createContext<PromptManager>(null);

export class PromptContainer extends Component<{ manager?: PromptManager }> {
    static contextType = PromptContext;

    render() {
        return <InnerPromptContainer manager={this.props.manager || this.context} />
    }
}

export class PromptProvider extends Component<{ manager?: PromptManager }> {

    private _manager: PromptManager;
    get manager() {
        return this.props.manager || this._manager || (this._manager = new PromptManager())
    }

    render() {
        return <PromptContext.Provider value={this.manager} children={this.props.children} />
    }
}
